"use client"

import { useState } from "react"
import styles from "./styles.module.css"
import Select from "../../modules/Select/Select"
import Button from "../../modules/Button/Button"
import SerialInput from "../../modules/SerialInput"
import toast from "react-hot-toast"
import DatePicker from "../../../shared-modules/DatePicker/DatePicker"
import { parseDateSafe } from "../../../../utils/dateFormatters"

const INITIAL_DEVICE_STATE = {
  serial_number: "",
  device_type: "",
  inventory_status: "In Warehouse",
  purchase_date: null, // required now
}

const AddDevice = ({ setHandler, boxDetails }) => {
  const [deviceDetails, setDeviceDetails] = useState({
    ...INITIAL_DEVICE_STATE,
    inventory_status: boxDetails.inventory_status || "In Warehouse",
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const compatibleDeviceTypes = boxDetails.compatible_device_types || []
  const deviceTypeOptions = compatibleDeviceTypes.map((type) => ({
    label: type.name,
    value: type.id,
  }))

  // ---- Validation ----
  const validateForm = () => {
    const newErrors = {}
    if (!deviceDetails.serial_number.trim()) {
      newErrors.serial_number =
        deviceDetails.device_type_name === "Tenovi Hub" ? "Gateway ID is required" : "IMEI number is required"
    } else {
      if (deviceDetails.device_type_name === "Tenovi Hub") {
        if (!/^[0-9A-Fa-f]{12}$/.test(deviceDetails.serial_number)) {
          newErrors.serial_number = "Gateway ID must be 12 hex characters"
        }
      } else {
        if (!/^\d{15}$/.test(deviceDetails.serial_number)) {
          newErrors.serial_number = "IMEI must be exactly 15 digits"
        }
      }
    }

    if (!deviceDetails.device_type) {
      newErrors.device_type = "Device type is required"
    }

    // purchase_date is mandatory for bulk add
    if (!deviceDetails.purchase_date) {
      newErrors.purchase_date = "Purchase date is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setDeviceDetails((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const addDeviceHandler = async () => {
    if (!validateForm()) return

    setIsLoading(true)
    try {
      const serial = deviceDetails.serial_number

      const deviceToAdd = {
        serial,
        device_type: deviceDetails.device_type,
        inventory_status: deviceDetails.inventory_status,
        box_id: boxDetails.id,
        // keep legacy timestamp if some consumers expect it
        date: new Date().getTime(),
        // new purchase_date field (ISO)
        ...(deviceDetails.purchase_date && { purchase_date: parseDateSafe(deviceDetails.purchase_date)?.toISOString() || new Date().toISOString() }),
      }

      await setHandler(deviceToAdd)
      setDeviceDetails({
        ...INITIAL_DEVICE_STATE,
        inventory_status: boxDetails.inventory_status || "In Warehouse",
      })
      setErrors({})
    } catch (err) {
      console.error("AddDevice error:", err)
      toast.error("Failed to add device. See console.")
    } finally {
      setIsLoading(false)
    }
  }

  // get selected deviceType name
  const selectedTypeObj = compatibleDeviceTypes.find((t) => String(t.id) === String(deviceDetails.device_type))
  deviceDetails.device_type_name = selectedTypeObj?.name

  return (
    <section className={styles.container}>
      <section className={styles.inputContainer}>
        {/* Device Type */}
        <section className={styles.inputField}>
          <label className={styles.label}>Device Type *</label>
          <Select
            options={deviceTypeOptions}
            value={deviceDetails.device_type}
            onChange={(value) => handleInputChange("device_type", value)}
            placeholder="Select device type"
            width="100%"
          />
          {errors.device_type && <span className={styles.error}>{errors.device_type}</span>}
        </section>

        {/* Serial / Gateway ID */}
        <div className={styles.inputField}>
          <label className={styles.label}>
            {deviceDetails.device_type_name === "Tenovi Hub" ? "Gateway ID (Hub)" : "IMEI Number"}
          </label>
          <SerialInput
            value={deviceDetails.serial_number}
            onChange={(val) => handleInputChange("serial_number", val)}
            deviceType={deviceDetails.device_type_name}
            disabled={!deviceDetails.device_type}
            error={errors.serial_number}
          />
        </div>

        {/* Purchase Date (required) */}
        <section className={styles.inputField}>
          <label className={styles.label}>Purchase Date *</label>
          <DatePicker
            selectedDate={deviceDetails.purchase_date}
            onDateSelect={(date) => handleInputChange("purchase_date", date)}
            placeholder="Select purchase date"
          />
          {errors.purchase_date && <span className={styles.error}>{errors.purchase_date}</span>}
        </section>
      </section>

      <section className={styles.actionContainer}>
        <Button variant="primary" size="small" onClick={addDeviceHandler} loading={isLoading}>
          Add Device
        </Button>
      </section>
    </section>
  )
}

export default AddDevice